// "How this works" section at the bottom of the explorer (anchor
// #methodology). States what the tool is and is not, the model used, the
// sources, the non-affiliation notice, and publishes the exact system
// prompt verbatim in collapsible panels.

import type { ManifestoLanguage } from '../../lib/useManifestoQuery';
import { RULES_EN, RULES_MT } from '../../data/manifestoRules';
import meta from '../../data/manifestos/manifest-meta.json';
import './MethodologyNotice.css';

const FEEDBACK_EMAIL = 'editorial@lovinmalta.com';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function MethodologyNotice({
  language,
}: {
  language: ManifestoLanguage;
}) {
  const en = language === 'en';
  const retrieved = formatDate(meta.extractedAt);
  const chapterCount = meta.pnChapters.length;

  return (
    <section className="mf-method" id="methodology">
      <h2 className="mf-method__title">
        {en ? 'How this works' : 'Kif taħdem din l-għodda'}
      </h2>


      <div className="mf-method__body">
        {en ? (
          <>
            <p>
              This is an experimental tool that compares the 2026 electoral
              manifestos of the Partit Laburista (PL) and the Partit
              Nazzjonalista (PN) side by side. It reads only the text of
              those two documents.
            </p>
            <p>
              <strong>What it does:</strong> it answers descriptive and
              comparative questions and attaches a page citation to every
              factual claim, so you can verify each point against the source
              PDF. Click any citation to open the manifesto at that page.
            </p>
            <p>
              <strong>What it does not do:</strong> it does not use outside
              knowledge, it does not tell you who to vote for, and it does not
              cover polling, candidates, scandals or party history. AI can make
              mistakes — always check the cited pages.
            </p>
            <p>
              <strong>Model:</strong> Claude Opus or Claude Sonnet
              (Anthropic), depending on demand.
            </p>
            <p>
              <strong>Sources:</strong> the full PL manifesto and all{' '}
              {chapterCount} PN chapter documents, retrieved {retrieved}.
            </p>
            <p>
              <strong>Limits:</strong> there may be usage limits depending
              on traffic.
            </p>
            <p>
              Found a problem or a wrong answer?{' '}
              <a href={`mailto:${FEEDBACK_EMAIL}?subject=Manifesto%20Explorer%20feedback`}>
                Report it here
              </a>
              .
            </p>
          </>
        ) : (
          <>
            <p>
              Din hija għodda sperimentali li tqabbel il-manifesti elettorali
              tal-2026 tal-Partit Laburista (PL) u tal-Partit Nazzjonalista (PN)
              ħdejn xulxin. Taqra biss it-test ta' dawn iż-żewġ dokumenti.
            </p>
            <p>
              <strong>X'tagħmel:</strong> twieġeb mistoqsijiet deskrittivi u
              komparattivi u tehmeż ċitazzjoni tal-paġna ma' kull dikjarazzjoni
              fattwali, biex tkun tista' tivverifika kull punt mas-sors. Agħfas
              fuq kwalunkwe ċitazzjoni biex tiftaħ il-manifest f'dik il-paġna.
            </p>
            <p>
              <strong>X'ma tagħmilx:</strong> ma tużax għarfien minn barra, ma
              tgħidlekx lil min għandek tivvota, u ma tkoprix stħarriġ,
              kandidati, skandli jew storja tal-partiti. L-IA tista' tagħmel
              żbalji — dejjem iċċekkja l-paġni ċċitati.
            </p>
            <p>
              <strong>Mudell:</strong> Claude Opus jew Claude Sonnet
              (Anthropic), skont id-domanda.
            </p>
            <p>
              <strong>Sorsi:</strong> il-manifest sħiħ tal-PL u t-{chapterCount}{' '}
              dokumenti tal-kapitli tal-PN, miġbura fl-{retrieved}.
            </p>
            <p>
              <strong>Limiti:</strong> jistgħu japplikaw limiti fuq l-użu skont
              it-traffiku.
            </p>
            <p>
              Sibt problema jew tweġiba żbaljata?{' '}
              <a href={`mailto:${FEEDBACK_EMAIL}?subject=Manifesto%20Explorer%20feedback`}>
                Irrappurtaha hawn
              </a>
              .
            </p>
          </>
        )}
      </div>

      <details className="mf-method__prompt">
        <summary>
          {en
            ? 'Show the exact instructions given to the AI (English)'
            : 'Uri l-istruzzjonijiet eżatti mogħtija lill-IA (Ingliż)'}
        </summary>
        <pre>{RULES_EN}</pre>
      </details>
      <details className="mf-method__prompt">
        <summary>
          {en
            ? 'Show the exact instructions given to the AI (Maltese)'
            : 'Uri l-istruzzjonijiet eżatti mogħtija lill-IA (Malti)'}
        </summary>
        <pre>{RULES_MT}</pre>
      </details>
    </section>
  );
}
